import * as React from "react";
import {
  Datagrid,
  List,
  NumberField,
  TextField,
  TopToolbar,
  useGetOne,
  useRecordContext,
  useRefresh,
} from "react-admin";
import useNotify from "../unit/useNotify";

import PostPagination from "../unit/PostPagination";

import CustomDateField from "../unit/CustomDateField";
import CustomExportButton from "../unit/CustomExportButton";
import CustomLoading from "../unit/CustomLoading";

import ApplicationLinkApplicationIdField from "../applications/ApplicationLinkApplicationIdField";
import CampaignsListAside from "../campaigns/CampaignsListAside";
import CampaignsApplicationsFilters from "../campaigns_applications/CampaignsApplicationsFilters";
import CustomerIdLinkUserIdField from "../customers/CustomerIdLinkUserIdField";
import { hmLightTheme } from "../themes/hmTheme";
import CustomAmountField from "../unit/CustomAmountField";
import CustomFilterButton from "../unit/CustomFilterButton";
import CouponCodesApplicationsFilters from "./CouponCodesApplicationsFilters";
import { Typography } from "@mui/material";
import HmLoading from "../unit/HmLoading";
import NoResults from "../unit/NoResults";
import NoPermission from "../unit/NoPermission";
import formatDate from "../unit/formatDate";
const CampaignsListActions = ({ ...props }) => {
  return (
    <>
      <TopToolbar sx={{ width: "100%" }}>
        <CustomFilterButton />
        <CustomExportButton />
      </TopToolbar>
    </>
  );
};
const CustomCouponCodeValueField = ({ ...props }) => {
  const record = useRecordContext();
  if (!record) return null;
  let dAmount = record?.dAmount;
  let fValue = record?.fValue;
  let sType = record?.sType;
  return (
    <>
      {sType == "amount" ? (
        <NumberField
          source="fValue"
          options={{
            style: "currency",
            currency: "USD",
            currencyDisplay: "narrowSymbol",
            minimumFractionDigits: 2,
          }}
          sx={{
            fontFamily: hmLightTheme?.palette?.numberFontFamily,
            textAlign: "right",
            display: "inline-block",
            width: "100%",
          }}
        />
      ) : (
        <>{fValue}%</>
      )}
    </>
  );
};

const couponcodeApplicationsTypeExporter = (records: any) => {
  let data = [];
  let title = {};

  data = records.map((record: any) => {
    const { id } = record;
    return {
      iApplicationId: record?.iApplicationId,
      iUserId: record?.iUserId,
      sCouponCode: record?.sCouponCode,
      dAmount: record?.dAmount,
      fValue: record?.fValue,
      signUpTime: formatDate(record?.signUpTime, "YYYY-MM-DD"),
      dtDocusignComplete: formatDate(record?.dtDocusignComplete, "YYYY-MM-DD"),
      sProductName: record?.sProductName,
    };
  });
  title = {
    iApplicationId: "App ID",
    iUserId: "User ID",
    sCouponCode: "Coupon Code",
    dAmount: "Amount",
    fValue: "Reduction",
    signUpTime: "Applicant Sign Up",
    dtDocusignComplete: "Submitted On",
    sProductName: "Product",
  };
  return {
    title: title,
    data: data,
  };
};
const CustomExporter = (records: any) => {
  const filterData = couponcodeApplicationsTypeExporter(records);

  let csvTitle = filterData.title;
  let data = filterData.data;
  const csvData = [{ ...csvTitle }, ...data];
  const csvDataStr = csvData.map((row) => Object.values(row).join(","));
  downloadCSV(csvDataStr.join("\n"), "Coupon Codes");
};

const CouponcodeApplicationsList = () => {
  const refresh = useRefresh();
  const [open, setOpen] = React.useState(false);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const [record, setRecord] = React.useState<{ total: number } | null>(null);
  const permissions =
    localStorage.getItem("bbaccess")?.toString().split(",") || [];
  if (!permissions.includes("personal_campaigns")) {
    return <NoPermission />;
  }
  const { data, isLoading, error } = useGetOne("total/campaign", { id: 1 });
  React.useEffect(() => {
    setRecord(data);
  }, [data]);

  const total = record ? record.total : 0;

  const CustomRowStyle = (record: any) => {
    if (!record) return {};

    if (record?.sStatus != "Declined" && record?.sStatus != "Expired") {
      return {
        backgroundColor: "#fff",
        "&.MuiTableRow-hover:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      };
    } else {
      return {
        backgroundColor: "#f5f5f5",
        "& td.MuiTableCell-root": {
          color: "gray",
        },
        "& td.MuiTableCell-root span": {
          color: "gray",
        },
        "& td.MuiTableCell-root a": {
          color: "gray",
        },
        "& td.MuiTableCell-root p": {
          color: "gray",
        },
      };
    }
  };
  return (
    <>
      <List
        actions={<CampaignsListActions />}
        filters={CouponCodesApplicationsFilters}
        aside={
          <CampaignsListAside
            totalData={total}
            selected="couponcodesToApplications"
          />
        }
        exporter={CustomExporter}
        sort={{ field: "created_at", order: "DESC" }}
        sx={{
          width: "100%",
          borderRadius: "0px",
          "& .MuiCard-root": {
            borderRadius: "0px",
          },
          "& .RaList-actions form": {
            flex: "0 1 350%",
          },
          "& .RaList-actions form .filter-field .MuiInputBase-input": {
            width: "230px",
          },
          "& .RaList-actions .RaFilterForm-filterFormInput:first-child .MuiInputBase-root::before":
            {
              display: "none",
            },
          "& .RaList-actions .filter-field:first-child .MuiFormControl-root": {
            marginLeft: 0,
          },
        }}
        pagination={<PostPagination />}
        perPage={25}
        empty={false}
        emptyWhileLoading={false}
        loading={<HmLoading />}
      >
        <CustomLoading />
        <Datagrid
          optimized
          rowClick={false}
          empty={false}
          size="small"
          id="table-campaigns"
          className="hm-list-datagrid"
          sx={{
            ".MuiTableCell-root": {
              padding: "6px",
            },
            ".RaDatagrid-headerCell:first-child": {
              borderTopLeftRadius: "0px",
            },
            ".RaDatagrid-headerCell:last-child": {
              borderTopRightRadius: "0px",
            },
            overflowY: "auto",
            "& .column-groups": {
              md: { display: "none" },
              lg: { display: "table-cell" },
            },
            "& .Mui-active": {
              color: "gray",
            },
            "& .MuiTableSortLabel-root": {
              padding: "5px",
            },
            "& .RaDatagrid-headerCell": {
              color: "gray",
              fontSize: "1rem",
              backgroundColor: "#edf4fb",
              wordWrap: "break-word",
            },
            "& .MuiTableCell-paddingCheckbox": {
              display: "none",
            },
            "& .MuiTableCell-root.column-id": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
            },
            "& .MuiTableCell-root.column-sUrl": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-sName": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-created_at": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-dAmount": {
              textAlign: "right",
            },
            "& .MuiTableCell-root.column-fValue": {
              textAlign: "right",
            },
            "& .MuiTableCell-root.column-column-action": {
              minWidth: "250px",
              width: "250px",
              maxWidth: "250px",
            },
          }}
        >
          <ApplicationLinkApplicationIdField
            source="iApplicationId"
            label="App #"
            sortBy={"iApplicationId"}
            sortable={false}
          />
          <CustomerIdLinkUserIdField
            source="iUserId"
            label="User #"
            sortBy={"iUserId"}
            sortable={false}
          />
          <TextField
            source="sCouponCode"
            label="Coupon Code"
            sortable={false}
          />
          <CustomAmountField
            source="dAmount"
            label="Amount"
            sortBy="dAmount"
            cellClassName="amountText"
            sortable={false}
          />
          <CustomCouponCodeValueField
            source="fValue"
            label="Reduction"
            sortBy="dAmount"
            cellClassName="amountText"
            sortable={false}
          />
          <CustomDateField
            source="signUpTime"
            label="Applicant Sign Up"
            onlyShowDate={true}
            sortable={false}
            cellClassName="dateText"
          />
          <CustomDateField
            source="dtDocusignComplete"
            label="Submitted On"
            onlyShowDate={true}
            sortable={false}
            cellClassName="dateText"
          />
          <TextField source="sProductName" label="Product" sortable={false} />
        </Datagrid>
      </List>
    </>
  );
};

export default CouponcodeApplicationsList;
